import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconRatingGood } from '@components/icons/rating-good';
import { IconRatingMedium } from '@components/icons/rating-medium';
import { IconRatingPoor } from '@components/icons/rating-poor';
import { IconRatingVeryGood } from '@components/icons/rating-very-good';
import { IconRatingVeryPoor } from '@components/icons/rating-very-poor';
import { IconTextShort } from '@components/icons/text-short';
import { Stack } from '@components/stack';
import { Text } from '@components/text';

import { Modal, ModalProps } from 'features/modals';
import { useStore } from 'models/Provider';

import { HireflixQuestion, HireflixUsersRating } from './types';

type Props = ModalProps<{
  questions: HireflixQuestion[];
  ratings: HireflixUsersRating;
}>;

function getIconByRating(rating: number) {
  if (rating === 0) {
    return <IconRatingVeryPoor color="red200" />;
  } else if (rating === 2.5) {
    return <IconRatingPoor color="red100" />;
  } else if (rating === 5) {
    return <IconRatingMedium color="orange100" />;
  } else if (rating === 7.5) {
    return <IconRatingGood color="green100" />;
  } else if (rating === 10) {
    return <IconRatingVeryGood color="green200" />;
  }
  return <IconRatingMedium />;
}

export const HireflixReviewsModal = observer((props: Props) => {
  const { closeModal, questions, ratings } = props;
  const { t, userStore } = useStore();

  return (
    <Modal>
      <Modal.Header text={t('integrations.hireflix.application.modals.reviews.header')} />
      <Stack space={4}>
        {questions.map((question) => (
          <Stack space={2} key={question.id}>
            <Stack space={1} orientation="horizontal" align="center">
              <IconTextShort />
              <Text font="bold14" text={question.title} />
            </Stack>
            {ratings[question.id] &&
              ratings[question.id].map((rating) => (
                <Card space={2} key={rating.userId}>
                  <Stack space={0} orientation="horizontal" justify="space-between">
                    <Text font="regular14" text={userStore.items.get(rating.userId).name} />
                    {getIconByRating(rating.rating)}
                  </Stack>
                </Card>
              ))}
          </Stack>
        ))}
      </Stack>
      <Modal.Actions>
        <Button variant="secondary" onClick={closeModal} text={t('modals.confirm.button.close')} />
      </Modal.Actions>
    </Modal>
  );
});
